'use client';
import React, { useState, useEffect } from 'react';

interface NavItem {
  label: string;
  href: string;
  dropdown?: { title: string; desc: string; href: string }[];
}

const navItems: NavItem[] = [
  {
    label: 'About Us',
    href: '/about',
    dropdown: [
      { title: 'Our Story', desc: 'Building communities since 1973.', href: '/about' },
      { title: 'Mission & Vision', desc: 'What drives us forward.', href: '/about' },
      { title: 'Leadership', desc: 'Meet our executive team.', href: '/about' },
      { title: 'Leadership Directory', desc: 'Talk to us about your housing needs.', href: '/about' },
    ],
  },
  {
    label: 'Portfolio',
    href: '/portfolio',
    dropdown: [
      { title: 'Affordable', desc: "Quality housing for every budget.", href: '/portfolio' },
      { title: 'Attainable', desc: 'Innovating ways to make housing accessible.', href: '/portfolio' },
      { title: 'Market-Rate', desc: 'Comfortable living in vibrant communities.', href: '/portfolio' },
      { title: 'Senior Living', desc: 'Thoughtful homes for active adults.', href: '/portfolio' },
    ],
  },
  {
    label: 'Expertise',
    href: '/expertise',
    dropdown: [
      { title: 'Development', desc: 'You can trust your vision to us.', href: '/expertise#development' },
      { title: 'Management', desc: 'Ensuring residents and communities thrive.', href: '/expertise#management' },
      { title: 'Construction', desc: 'Integrity built into everything we do.', href: '/expertise#construction' },
      { title: 'Investment', desc: 'Always looking for new partners and properties.', href: '/expertise#investment' },
    ],
  },
  {
    label: 'Media',
    href: '/media-center',
    dropdown: [
      { title: 'Featured Story', desc: "What's new at Brickwell?", href: '/media-center' },
      { title: 'Latest News', desc: 'Check out our latest updates.', href: '/media-center' },
      { title: 'Press Kit', desc: 'Download our media resources.', href: '/media-center' },
    ],
  },
  {
    label: 'Careers',
    href: '/careers',
    dropdown: [
      { title: 'Why Brickwell?', desc: 'Grow your career and make a difference.', href: '/careers#why-brickwell' },
      { title: 'Culture & Rewards', desc: 'Our commitment to our team.', href: '/careers#values-benefits-dei' },
      { title: 'Beyond the Job', desc: 'Endless opportunities for growth.', href: '/careers#beyond-the-job' },
      { title: 'Our Teams', desc: 'A place for everyone.', href: '/careers#our-teams' },
      { title: 'View Available Jobs', desc: 'Come build with us.', href: 'https://careers-tmo.icims.com/jobs/search' },
    ],
  },
  {
    label: 'Contact Us',
    href: '/contact',
  },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleMobileDropdown = (label: string) => {
    setOpenDropdown(openDropdown === label ? null : label);
  };

  return (
    <header
      className={`bw-header ${scrolled ? 'scrolled' : ''}`}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1000,
        transition: 'background-color 0.3s ease, box-shadow 0.3s ease',
        backgroundColor: scrolled ? '#ffffff' : 'transparent',
        boxShadow: scrolled ? '0 2px 20px rgba(0,0,0,0.1)' : 'none',
      }}
    >
      <div style={{ maxWidth: '1280px', margin: '0 auto', padding: '0 24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: '80px' }}>
          {/* Logo */}
          <a href="/" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{
              width: '44px',
              height: '44px',
              backgroundColor: '#003c82',
              borderRadius: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" fill="#6dcff6" />
                <polyline points="9 22 9 12 15 12 15 22" fill="#ffffff" />
              </svg>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1 }}>
              <span style={{
                fontSize: '20px',
                fontWeight: '700',
                color: scrolled ? '#003c82' : '#ffffff',
                fontFamily: 'Montserrat, sans-serif',
                letterSpacing: '-0.02em',
                transition: 'color 0.3s ease',
              }}>Brickwell</span>
            </div>
          </a>

          {/* Desktop Nav */}
          <nav className="desktop-nav" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            {navItems.map((item) => (
              <div key={item.label} className="nav-item" style={{ position: 'relative' }}>
                <a
                  href={item.href}
                  className="nav-link"
                  style={{
                    color: scrolled ? '#333333' : '#ffffff',
                    fontSize: '14px',
                    fontWeight: '500',
                    textDecoration: 'none',
                    padding: '8px 12px',
                    transition: 'color 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    fontFamily: 'Montserrat, sans-serif',
                  }}
                >
                  {item.label}
                  {item.dropdown && (
                    <svg width="10" height="6" viewBox="0 0 10 6" fill="none">
                      <path d="M1 1l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                </a>
                {item.dropdown && (
                  <div className="dropdown-menu" style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    background: '#ffffff',
                    minWidth: '280px',
                    boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
                    zIndex: 100,
                    padding: '8px 0',
                  }}>
                    {item.dropdown.map((d) => (
                      <a key={d.title} href={d.href} className="dropdown-item" style={{
                        display: 'block',
                        padding: '12px 20px',
                        textDecoration: 'none',
                        color: '#333333',
                        transition: 'background-color 0.2s ease',
                      }}>
                        <strong style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#003c82' }}>{d.title}</strong>
                        <span style={{ display: 'block', fontSize: '12px', color: '#666', marginTop: '2px' }}>{d.desc}</span>
                      </a>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </nav>

          {/* CTA Button + Hamburger */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <a
              href="https://www.michaelscommunities.com/properties"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary desktop-cta"
              style={{
                backgroundColor: '#003c82',
                color: '#ffffff',
                padding: '10px 20px',
                fontSize: '13px',
                fontWeight: '600',
                textDecoration: 'none',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                borderRadius: '2px',
                fontFamily: 'Montserrat, sans-serif',
                whiteSpace: 'nowrap',
              }}
            >
              Find Your Home
            </a>
            <button
              className="mobile-nav-toggle"
              onClick={() => setMobileOpen(!mobileOpen)}
              style={{
                flexDirection: 'column',
                gap: '5px',
                cursor: 'pointer',
                padding: '8px',
                background: 'none',
                border: 'none',
              }}
              aria-label="Toggle navigation"
            >
              {mobileOpen ? (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M18 6L6 18M6 6l12 12" stroke={scrolled ? '#333333' : '#ffffff'} strokeWidth="2" strokeLinecap="round" />
                </svg>
              ) : (
                <>
                  <span style={{ display: 'block', width: '24px', height: '2px', backgroundColor: scrolled ? '#333333' : '#ffffff' }} />
                  <span style={{ display: 'block', width: '24px', height: '2px', backgroundColor: scrolled ? '#333333' : '#ffffff' }} />
                  <span style={{ display: 'block', width: '24px', height: '2px', backgroundColor: scrolled ? '#333333' : '#ffffff' }} />
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div style={{
          backgroundColor: '#ffffff',
          borderTop: '1px solid #e0e0e0',
          maxHeight: '80vh',
          overflowY: 'auto',
        }}>
          {navItems.map((item) => (
            <div key={item.label} style={{ borderBottom: '1px solid #f0f0f0' }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '14px 24px',
                  cursor: item.dropdown ? 'pointer' : 'default',
                }}
                onClick={() => item.dropdown && toggleMobileDropdown(item.label)}
              >
                <a
                  href={item.href}
                  onClick={(e) => { if (item.dropdown) e.preventDefault(); else setMobileOpen(false); }}
                  style={{
                    color: '#333333',
                    textDecoration: 'none',
                    fontSize: '15px',
                    fontWeight: '600',
                    fontFamily: 'Montserrat, sans-serif',
                    flex: 1,
                  }}
                >
                  {item.label}
                </a>
                {item.dropdown && (
                  <svg
                    width="12"
                    height="8"
                    viewBox="0 0 10 6"
                    fill="none"
                    style={{
                      transform: openDropdown === item.label ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 0.2s ease',
                      flexShrink: 0,
                    }}
                  >
                    <path d="M1 1l4 4 4-4" stroke="#003c82" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
              </div>

              {/* Mobile Dropdown Items */}
              {item.dropdown && openDropdown === item.label && (
                <div style={{ backgroundColor: '#f8f9fa', paddingBottom: '8px' }}>
                  {item.dropdown.map((d) => (
                    <a
                      key={d.title}
                      href={d.href}
                      onClick={() => setMobileOpen(false)}
                      style={{
                        display: 'block',
                        padding: '10px 24px 10px 32px',
                        textDecoration: 'none',
                        color: '#333333',
                        borderBottom: '1px solid #eeeeee',
                      }}
                    >
                      <strong style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#003c82' }}>{d.title}</strong>
                      <span style={{ display: 'block', fontSize: '12px', color: '#666', marginTop: '2px' }}>{d.desc}</span>
                    </a>
                  ))}
                  <a
                    href={item.href}
                    onClick={() => setMobileOpen(false)}
                    style={{
                      display: 'block',
                      padding: '10px 24px 10px 32px',
                      textDecoration: 'none',
                      color: '#003c82',
                      fontSize: '13px',
                      fontWeight: '600',
                      fontFamily: 'Montserrat, sans-serif',
                    }}
                  >
                    View All →
                  </a>
                </div>
              )}
            </div>
          ))}

          <div style={{ padding: '16px 24px' }}>
            <a
              href="https://www.michaelscommunities.com/properties"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setMobileOpen(false)}
              style={{
                display: 'block',
                backgroundColor: '#003c82',
                color: '#ffffff',
                padding: '12px 20px',
                textDecoration: 'none',
                fontSize: '14px',
                fontWeight: '600',
                textAlign: 'center',
                borderRadius: '2px',
                fontFamily: 'Montserrat, sans-serif',
              }}
            >
              Find Your Home
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
